import { roundUsd } from './format';
import { transactionFlow } from './report';
import type { FinanceCategoryKind } from './types';

export type BalanceAccount = {
  id: number;
  name: string;
  currencyCode: string;
  isActive: boolean;
  sortOrder: number;
};

export type BalanceTransaction = {
  date: string;
  accountId: number | null;
  toAccountId: number | null;
  categoryId: number | null;
  currencyCode: string;
  amount: number;
};

export type BalanceCategory = {
  id: number;
  kind: FinanceCategoryKind;
};

type Running = {
  amount: number;
  foreignCount: number;
  lastDate: string | null;
};

export type AccountBalanceRow = {
  accountId: number;
  name: string;
  currencyCode: string;
  isActive: boolean;
  /** Native units. Transactions in another currency are excluded. */
  amount: number;
  /** Transactions on this account whose currency does not match it. */
  foreignCount: number;
  /** Latest transaction date on this account through `asOf`, if any. */
  lastTransactionDate: string | null;
  /** Native amount converted at the as-of month's rate. Null when that rate is missing. */
  usd: number | null;
};

export type BalanceSnapshot = {
  asOf: string;
  rows: AccountBalanceRow[];
  /** Sum of `usd` when every non-zero balance could be converted. */
  totalUsd: number | null;
  missingCurrencies: string[];
  /** Transactions through `asOf` that name no account. */
  unassignedCount: number;
};

export type FxRatePoint = {
  month: string;
  currencyCode: string;
  rateToUsd: number | null;
};

export type BalancePoint = {
  month: string;
  asOf: string;
  totalUsd: number | null;
  rows: AccountBalanceRow[];
};

function flowOf(
  tx: BalanceTransaction,
  kindById: Map<number, FinanceCategoryKind>,
): FinanceCategoryKind {
  return transactionFlow(kindById, tx);
}

function touch(
  running: Map<number, Running>,
  accountsById: Map<number, BalanceAccount>,
  accountId: number,
  tx: BalanceTransaction,
  delta: number,
): void {
  const account = accountsById.get(accountId);
  if (!account) return;
  let row = running.get(accountId);
  if (!row) {
    row = { amount: 0, foreignCount: 0, lastDate: null };
    running.set(accountId, row);
  }
  if (row.lastDate == null || tx.date > row.lastDate) row.lastDate = tx.date;
  if (tx.currencyCode !== account.currencyCode) {
    row.foreignCount += 1;
    return;
  }
  row.amount += delta;
}

function applyTransaction(
  running: Map<number, Running>,
  accountsById: Map<number, BalanceAccount>,
  tx: BalanceTransaction,
  kind: FinanceCategoryKind,
): void {
  const magnitude = Math.abs(tx.amount);
  if (kind === 'transfer') {
    const inbound = tx.amount < 0;
    if (tx.accountId != null) {
      touch(running, accountsById, tx.accountId, tx, inbound ? magnitude : -magnitude);
    }
    if (tx.toAccountId != null && tx.toAccountId !== tx.accountId) {
      touch(running, accountsById, tx.toAccountId, tx, inbound ? -magnitude : magnitude);
    }
    return;
  }
  if (tx.accountId != null) {
    touch(running, accountsById, tx.accountId, tx, kind === 'income' ? magnitude : -magnitude);
  }
}

function isUnassigned(tx: BalanceTransaction, kind: FinanceCategoryKind): boolean {
  if (kind === 'transfer') return tx.accountId == null && tx.toAccountId == null;
  return tx.accountId == null;
}

function rateIndex(rates: FxRatePoint[]): Map<string, number | null> {
  const map = new Map<string, number | null>();
  for (const rate of rates) {
    map.set(`${rate.month.slice(0, 7)}:${rate.currencyCode}`, rate.rateToUsd);
  }
  return map;
}

function nativeToUsd(
  amount: number,
  currencyCode: string,
  rates: Map<string, number | null>,
  month: string,
): number | null {
  if (currencyCode === 'USD') return roundUsd(amount);
  const rate = rates.get(`${month}:${currencyCode}`);
  if (rate == null || rate === 0) return null;
  return roundUsd(amount / rate);
}

/**
 * A USD amount in `currencyCode` at `month`'s rate. `rateToUsd` is units of that
 * currency per 1 USD. Zero stays zero without a rate. Null when `usd` is null
 * or a non-USD rate is missing.
 */
export function usdInCurrency(
  usd: number | null,
  currencyCode: string,
  month: string,
  rates: FxRatePoint[],
): number | null {
  if (usd == null) return null;
  if (currencyCode === 'USD' || usd === 0) return usd;
  const key = month.slice(0, 7);
  const rate = rates.find(
    (point) => point.currencyCode === currencyCode && point.month.slice(0, 7) === key,
  )?.rateToUsd;
  if (rate == null || rate === 0) return null;
  return roundUsd(usd * rate);
}

/** The USD total expressed in `currencyCode` at the as-of month's rate. */
export function totalInCurrency(
  snapshot: Pick<BalanceSnapshot, 'asOf' | 'totalUsd'>,
  currencyCode: string,
  rates: FxRatePoint[],
): number | null {
  return usdInCurrency(snapshot.totalUsd, currencyCode, snapshot.asOf, rates);
}

/**
 * USD sum of the chosen accounts. Null when any chosen account has a non-zero
 * balance and no FX rate. An empty selection is 0.
 */
export function sumSelectedUsd(
  rows: Pick<AccountBalanceRow, 'accountId' | 'amount' | 'usd'>[],
  selected: ReadonlySet<number>,
): number | null {
  let total = 0;
  for (const row of rows) {
    if (!selected.has(row.accountId)) continue;
    if (row.amount !== 0 && row.usd == null) return null;
    if (row.usd != null) total += row.usd;
  }
  return roundUsd(total);
}

function snapshotAt(
  accounts: BalanceAccount[],
  running: Map<number, Running>,
  unassignedCount: number,
  asOf: string,
  rates: Map<string, number | null>,
): BalanceSnapshot {
  const month = asOf.slice(0, 7);
  const rows: AccountBalanceRow[] = [];
  const missing = new Set<string>();
  let total = 0;
  let complete = true;

  const ordered = [...accounts].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
  for (const account of ordered) {
    const state = running.get(account.id) ?? { amount: 0, foreignCount: 0, lastDate: null };
    const amount = roundUsd(state.amount);
    if (!account.isActive && amount === 0 && state.foreignCount === 0) continue;
    const usd = nativeToUsd(amount, account.currencyCode, rates, month);
    if (amount !== 0 && usd == null) {
      complete = false;
      missing.add(account.currencyCode);
    } else if (usd != null) {
      total += usd;
    }
    rows.push({
      accountId: account.id,
      name: account.name,
      currencyCode: account.currencyCode,
      isActive: account.isActive,
      amount,
      foreignCount: state.foreignCount,
      lastTransactionDate: state.lastDate,
      usd,
    });
  }

  return {
    asOf,
    rows,
    totalUsd: complete ? roundUsd(total) : null,
    missingCurrencies: [...missing].sort(),
    unassignedCount,
  };
}

function prepare(
  accounts: BalanceAccount[],
  categories: BalanceCategory[],
  transactions: BalanceTransaction[],
): {
  accountsById: Map<number, BalanceAccount>;
  kindById: Map<number, FinanceCategoryKind>;
  sorted: BalanceTransaction[];
} {
  return {
    accountsById: new Map(accounts.map((account) => [account.id, account])),
    kindById: new Map(categories.map((category) => [category.id, category.kind])),
    sorted: [...transactions].sort((a, b) => a.date.localeCompare(b.date)),
  };
}

/** Balances through `asOf`, in each account's currency, plus a USD total. */
export function buildBalanceSnapshot(
  accounts: BalanceAccount[],
  categories: BalanceCategory[],
  transactions: BalanceTransaction[],
  asOf: string,
  rates: FxRatePoint[] = [],
): BalanceSnapshot {
  const { accountsById, kindById, sorted } = prepare(accounts, categories, transactions);
  const running = new Map<number, Running>();
  let unassignedCount = 0;
  for (const tx of sorted) {
    if (tx.date > asOf) break;
    const kind = flowOf(tx, kindById);
    if (isUnassigned(tx, kind)) unassignedCount += 1;
    applyTransaction(running, accountsById, tx, kind);
  }
  return snapshotAt(accounts, running, unassignedCount, asOf, rateIndex(rates));
}

/**
 * Month-end (or as-of) net worth in USD. Each point converts that date's
 * native balances at that month's FX rates. Balance carries forward when a
 * month has no new transactions.
 */
export function buildBalanceSeries(
  accounts: BalanceAccount[],
  categories: BalanceCategory[],
  transactions: BalanceTransaction[],
  points: { month: string; asOf: string }[],
  rates: FxRatePoint[] = [],
): BalancePoint[] {
  const { accountsById, kindById, sorted } = prepare(accounts, categories, transactions);
  const ordered = [...points].sort((a, b) => a.asOf.localeCompare(b.asOf));
  const rateMap = rateIndex(rates);
  const running = new Map<number, Running>();
  let unassignedCount = 0;
  let index = 0;
  return ordered.map((point) => {
    while (index < sorted.length && sorted[index]!.date <= point.asOf) {
      const tx = sorted[index]!;
      const kind = flowOf(tx, kindById);
      if (isUnassigned(tx, kind)) unassignedCount += 1;
      applyTransaction(running, accountsById, tx, kind);
      index += 1;
    }
    const snap = snapshotAt(accounts, running, unassignedCount, point.asOf, rateMap);
    return { month: point.month, asOf: point.asOf, totalUsd: snap.totalUsd, rows: snap.rows };
  });
}
